import { getDeviceId } from '../services/axios.service'

/**
 * Converts a DD-MM-YYYY date to YYYY-MM-DD and strips any time/timezone
 * portion (eg. user.birthday comes as a full "1989-01-02T00:00:00" ISO
 * datetime); leaves an already-plain YYYY-MM-DD date untouched
 */
function normalizeDate(value) {
	if (!value) return null

	const datePart = value.split('T')[0]
	const parts = datePart.split('-')

	if (parts[0].length === 2) {
		return `${parts[2]}-${parts[1]}-${parts[0]}`
	}

	return datePart
}

function normalizePhone(value) {
	return value ? value.replace(/\D/g, '') : ''
}

function capitalize(value) {
	return value ? value.charAt(0).toUpperCase() + value.slice(1) : value
}

function buildPassengers(passengersValues) {
	return passengersValues.map((passenger, idx) => ({
		fullName: passenger.name.name ? passenger.name.name : passenger.name,
		documentType: passenger.documenttype,
		documentNumber: passenger.documentnumber,
		birthDate: normalizeDate(passenger[`passenger_birthdate_${idx}`])
	}))
}

function buildBuyer({ buyerValues, user, buyerAddress }) {
	// Quando logado, os dados do comprador vem do cadastro do usuário, ja
	// que o formulário de comprador (BuyerData) so e exibido para visitantes.
	const fullName = user ? user.name : buyerValues.name
	const email = user ? user.email : buyerValues.email
	const cpf = user ? user.cpf : buyerValues.cpf
	const birthDate = normalizeDate(
		user ? user.birthday : buyerValues.buyer_birthdate
	)
	// O telefone do usuário logado vem do endpoint autenticar (user.phone,
	// repassado sem alterações pela action de login); visitante preenche
	// no formulário de comprador (BuyerData).
	const phone = normalizePhone(user ? user.phone : buyerValues.phonenumber)

	return {
		fullName,
		email,
		cpf,
		phone,
		birthDate,
		// A captura de CEP/endereco do comprador esta desativada no
		// formulario hoje (buyer-data.component.jsx); enviamos o que
		// estiver disponivel em buyerAddress, que fica vazio por enquanto.
		address: {
			zipCode: buyerAddress.zipCode || '',
			street: buyerAddress.street || '',
			number: '',
			complement: '',
			neighborhood: buyerAddress.neighborhood || '',
			cityName: buyerAddress.city || '',
			stateCode: buyerAddress.state || ''
		}
	}
}

function buildCreditCardInstrument(paymentValues) {
	return {
		kind: 'CreditCardToken',
		installments: Number(paymentValues.installments) || 1,
		cardNumber: paymentValues.cardNumber,
		cardholderName: paymentValues.cardPrintedName,
		cvv: paymentValues.securityCode,
		expiryMonth: paymentValues.cardMonthValidity,
		expiryYear: paymentValues.cardYearValidity,
		brand: capitalize(paymentValues.cardBrand)
	}
}

function buildMuvicardInstrument(paymentValues, user) {
	// Compra 1-clique: o cliente ativou o serviço no cartão (user.muvicardData.oneClickBuy).
	// Nesse fluxo a tela nao coleta CPF nem senha - autenticamos so pelo CPF do comprador
	// logado. Enviamos o CPF como cardNumber (o legado busca o Muvicard por ele e a guarda
	// FR-004a exige que bata com o comprador) e o flag oneClickBuy; o legado dispensa a senha.
	const isOneClickBuy = !!(user && user.muvicardData && user.muvicardData.oneClickBuy)

	if (isOneClickBuy) {
		return {
			kind: 'Muvicard',
			installments: Number(paymentValues.installments) || 1,
			cardNumber: (user.cpf || '').replace(/\D/g, ''),
			oneClickBuy: true
		}
	}

	return {
		kind: 'Muvicard',
		installments: Number(paymentValues.installments) || 1,
		cardNumber: paymentValues.muvicardCpf,
		password: paymentValues.password
	}
}

/**
 * Builds the { method, instrument } pair for POST carts/{cartId}/checkout,
 * or null when this payment method/scenario isn't migrated to the new
 * checkout yet (saved card, boleto) - those keep using
 * TicketSaleService.finalizePurchase.
 */
export function buildPayment(paymentValues, user) {
	// hasPublicBody só pode pagar com Muvicard - checagem de segurança aqui
	// (além da UI já forçar isso) porque essa regra não pode depender só do
	// formulário estar em sincronia com o redux.
	const hasPublicBody = user && user.hasPublicBody
	const method = hasPublicBody ? 'muvicard' : paymentValues.paymentMethod

	// Cartao digitado na hora; cartao salvo (cardId) ainda nao tem exemplo
	// de payload novo, entao fica no fluxo antigo.
	if ((!method || method === 'creditCard') && !paymentValues.cardId) {
		return {
			method: 'CreditCard',
			instrument: buildCreditCardInstrument(paymentValues)
		}
	}

	if (method === 'pix') {
		return { method: 'Pix', instrument: { kind: 'Pix' } }
	}

	if (method === 'muvicard') {
		return {
			method: 'Muvicard',
			instrument: buildMuvicardInstrument(paymentValues, user)
		}
	}

	return null
}

/**
 * Motivo da viagem — a CATEGORIA escolhida no select ("Trabalho", "Lazer/Turismo",
 * "Estudo", "Saúde", "Outro"). Vai para CAIXA.TRAVEL_REASON. Vazio/ausente vira null
 * (o backend guarda null, não string vazia). Diferente do comportamento antigo, que
 * colapsava categoria+texto livre num campo só e nunca gravava OTHER_TRAVEL_REASON.
 */
function buildTravelReason(travelReason) {
	const reason = (travelReason || '').trim()
	return reason || null
}

/**
 * Texto livre digitado quando a categoria é "Outro" — vai para CAIXA.OTHER_TRAVEL_REASON.
 * Só é enviado quando a categoria é exatamente "Outro"; caso contrário null.
 */
function buildOtherTravelReason(travelReason, otherTravelReason) {
	if ((travelReason || '').trim() !== 'Outro') return null
	return (otherTravelReason || '').trim() || null
}

/**
 * Builds the payload for POST carts/{cartId}/checkout
 */
export function buildCheckoutParams(
	{ buyerValues, paymentValues, passengersValues, travelReasonValues, otherTravelReasonValues },
	{ user, buyerAddress, originPortal, affiliateCode }
) {
	return {
		payment: buildPayment(paymentValues, user),
		buyer: buildBuyer({ buyerValues, user, buyerAddress }),
		passengers: buildPassengers(passengersValues),
		// Negócio: motivo da viagem (categoria + texto livre) e afiliado. O fluxo legado
		// (getParams) já os enviava; o checkout novo os omitia, então chegavam null no
		// MuviHub e o CAIXA gravava TRAVEL_REASON/OTHER_TRAVEL_REASON vazios. A categoria
		// vai em travelObjective (→ TRAVEL_REASON) e o texto livre de "Outro" em
		// otherTravelReason (→ OTHER_TRAVEL_REASON) — dois campos, como no nativo.
		travelObjective: buildTravelReason(travelReasonValues),
		otherTravelReason: buildOtherTravelReason(travelReasonValues, otherTravelReasonValues),
		affiliateCode: affiliateCode || null,
		context: {
			originPortal: originPortal || 'web',
			deviceId: getDeviceId(),
			callKonduto: true
		}
	}
}

/**
 * Maps one ticket from the checkout response (MuviHub field names) into the
 * shape ConfirmedTicketsComponent/OrderSummaryItem already know how to
 * render (legacy Portuguese field names). ticketId is dropped: unlike the
 * legacy flow, the new checkout always returns one, which would make the
 * "Bilhete" column show up with a raw internal id - not wanted here.
 */
function adaptConfirmedTicket(ticket, busCompanyLogoUrl) {
	return {
		...ticket,
		ticketId: null,
		origem: ticket.origin,
		destino: ticket.destination,
		dataPartida: ticket.departure,
		modalidade: ticket.busClass,
		busSeat: ticket.busSeatNumber,
		poltrona: ticket.busSeatNumber,
		documentType: ticket.passengerDocumentType,
		documentNumber: ticket.passengerDocumentNumber,
		busCompanyLogoUrl
	}
}

function adaptConfirmedTickets(tickets, busCompanyLogoUrl) {
	return (tickets || []).map(ticket =>
		adaptConfirmedTicket(ticket, busCompanyLogoUrl)
	)
}

/**
 * Adapts a successful checkout response's data into the shape
 * setConfirmedPurchaseData/confirmation-step.component.jsx already expect
 * (orderValue, pixData.{emv,qrCodeBase64,pixExpiration},
 * confirmedTicketsGoing/confirmedTicketsReturn), so nothing else downstream
 * needs to change.
 * @param {object} data The checkout response body
 * @param {object} logos { goingLogoUrl, returnLogoUrl } - the selected
 * trips' carrier logo, not present in the checkout response itself
 */
export function adaptCheckoutResponseData(data, logos = {}) {
	if (!data) return data

	const pix = data.paymentSummary && data.paymentSummary.pix
	const { goingLogoUrl, returnLogoUrl } = logos
	const allTickets = [...(data.ticketsGoing || []), ...(data.ticketsReturn || [])]

	return {
		...data,
		orderValue: data.totalPayable,
		confirmedTicketsGoing: adaptConfirmedTickets(data.ticketsGoing, goingLogoUrl),
		confirmedTicketsReturn: adaptConfirmedTickets(data.ticketsReturn, returnLogoUrl),
		showBpeDownloadLink: allTickets.some(ticket => ticket.showBpeDownloadLink),
		pixData: pix
			? {
				emv: pix.emv,
				qrCodeBase64: pix.qrCodeBase64,
				pixExpiration: pix.expiresAtUtc
			}
			: null
	}
}
